var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import { AsurionDiv, AsurionForm } from '../../BaseComponents';
import { SupportedPaymentMethodMappings } from '../../../services/ApplicationSession';
import { getLocaleDetails } from '../../Internationalization';
import { SubmitButton } from '../../SubmitButton/SubmitButton';
import { ComponentId, SupportedVendors } from '../../../common/Constants';
import { BillingAddressField } from '../../BillingAddress/BillingAddress';
import { getCardholderNameFromString } from '../CreditCard';
import { AsurionTheme } from '../../Theme';
import { brandMapping } from '../../../common/BrandMapping';
import { AddressErrorHandling } from '../../../services/ErrorService';
import { validCardholderName } from '../CreditCard';
var CreditCardWithCardholderName = styled(AsurionForm)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    display: flex;\n    flex-direction: column;\n    gap: ", ";\n"], ["\n    display: flex;\n    flex-direction: column;\n    gap: ", ";\n"])), function (props) { return props.theme.fieldGap; });
export var CreditCardRow = styled(AsurionDiv)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    display: flex;\n    flex-direction: row;\n    gap: ", ";\n\n    @media (max-width: 429px) {\n        flex-direction: column;\n    }\n"], ["\n    display: flex;\n    flex-direction: row;\n    gap: ", ";\n\n    @media (max-width: 429px) {\n        flex-direction: column;\n    }\n"])), function (props) { return props.theme.fieldGap; });
var initialCardFieldStatus = {
    number: {
        focused: false,
        empty: true
    },
    expirationDate: {
        focused: false,
        empty: true
    },
    cvv: {
        focused: false,
        empty: true
    }
};
var CreditCard = forwardRef(function (props, ref) {
    var _a, _b, _c, _d, _e;
    var formRef = useRef(null);
    var _f = useState(undefined), validityState = _f[0], setValidityState = _f[1];
    var _g = useState(props.existingBillingInformation), billingInformation = _g[0], setBillingInformation = _g[1];
    var billingAddressRef = useRef(billingInformation);
    var _h = useState(undefined), billingAddressValidityState = _h[0], setBillingAddressValidityState = _h[1];
    var billingAddressValidityStateRef = useRef(billingAddressValidityState);
    var _j = useState(undefined), cardValidityState = _j[0], setCardValidityState = _j[1];
    var cardValidityStateRef = useRef(cardValidityState);
    var _k = useState(initialCardFieldStatus), cardFieldStatuses = _k[0], setCardFieldStatuses = _k[1];
    var _l = useState(undefined), selectedCardBrand = _l[0], setSelectedCardBrand = _l[1];
    var _m = useState(undefined), cardholderName = _m[0], setCardholderName = _m[1];
    var cardholderNameRef = useRef(cardholderName);
    var _o = useState(undefined), supportedCardBrand = _o[0], setSupportedCardBrand = _o[1];
    var localeDetails = getLocaleDetails(props.vendor, props.locale);
    var CreditCardFields = localeDetails.creditCardComponent;
    var creditCardLocaleConfig = localeDetails.creditCard;
    // Used for custom continue button
    useImperativeHandle(ref, function () { return ({
        continue: function () {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.requestSubmit();
        }
    }); });
    var cardStyling = {
        input: {
            color: 'black',
            'font-smoothing': 'antialiased',
            'font-family': AsurionTheme.fontFamily,
            'font-size': '1rem',
            'font-weight': 300,
            'font-stretch': 'semi-condensed'
        }
    };
    useEffect(function () {
        billingAddressRef.current = billingInformation;
        billingAddressValidityStateRef.current = billingAddressValidityState;
        cardValidityStateRef.current = cardValidityState;
        cardholderNameRef.current = cardholderName;
    }, [billingInformation, billingAddressValidityState, cardValidityState, cardholderName]);
    useEffect(function () {
        if (props.existingCardholderName !== undefined) {
            if (typeof props.existingCardholderName === 'string') {
                setCardholderName(props.existingCardholderName);
            }
            else {
                setCardholderName("".concat(props.existingCardholderName.firstName || '', " ").concat(props.existingCardholderName.lastName || '').trim());
            }
        }
    }, [props.existingCardholderName]);
    useEffect(function () {
        if (cardholderName) {
            setBillingInformation(__assign(__assign({}, billingInformation), { contact: __assign(__assign({}, billingInformation === null || billingInformation === void 0 ? void 0 : billingInformation.contact), getCardholderNameFromString(cardholderName)) }));
            if (validityState) {
                setValidityState(__assign(__assign({}, validityState), { cardholderNameValid: validCardholderName(cardholderNameRef.current) }));
            }
        }
    }, [cardholderName]);
    useEffect(function () {
        var _a, _b, _c;
        (_a = props.onCreditCardFormValid) === null || _a === void 0 ? void 0 : _a.call(props, (validityState === null || validityState === void 0 ? void 0 : validityState.cardholderNameValid) === true &&
            ((_b = validityState === null || validityState === void 0 ? void 0 : validityState.creditCardValidity) === null || _b === void 0 ? void 0 : _b.isValid) === true &&
            ((_c = validityState === null || validityState === void 0 ? void 0 : validityState.billingAddressValidity) === null || _c === void 0 ? void 0 : _c.isValid) === true);
    }, [validityState]);
    var invalidFields = {
        invalidCardholderName: validityState !== undefined && !validityState.cardholderNameValid,
        invalidCardNumber: validityState !== undefined && !((_a = validityState.creditCardValidity) === null || _a === void 0 ? void 0 : _a.validCreditCardNumber),
        invalidCardBrand: cardValidityState !== undefined && !(cardValidityState === null || cardValidityState === void 0 ? void 0 : cardValidityState.validCardBrand),
        invalidExpiry: validityState !== undefined && (!((_b = validityState.creditCardValidity) === null || _b === void 0 ? void 0 : _b.validExpiryMonth) || !((_c = validityState.creditCardValidity) === null || _c === void 0 ? void 0 : _c.validExpiryYear)),
        invalidCvv: validityState !== undefined && !((_d = validityState.creditCardValidity) === null || _d === void 0 ? void 0 : _d.validCVV)
    };
    var onBrand = function (brand) {
        if (!brand) {
            setSelectedCardBrand(undefined);
            return;
        }
        var mappedBrand = brandMapping(SupportedVendors.braintree, [brand]);
        setSelectedCardBrand(mappedBrand[0]);
    };
    var onCardFieldsStatusChange = function (fields) {
        setCardFieldStatuses(fields);
    };
    var onValidityChange = function (validity) {
        setCardValidityState(validity);
    };
    var onChangeBillingAddress = function (address, addressValidityState) {
        setBillingInformation(__assign(__assign({}, billingInformation), { address: address }));
        setBillingAddressValidityState(addressValidityState);
    };
    var onSubmit = function (formFields) {
        var _a, _b, _c, _d;
        props.onProcessing(true);
        var cardholderNameValid = validCardholderName(cardholderNameRef.current);
        setValidityState({
            cardholderNameValid: cardholderNameValid,
            creditCardValidity: cardValidityStateRef.current,
            billingAddressValidity: billingAddressValidityStateRef.current
        });
        if (!cardholderNameValid || !((_a = cardValidityStateRef.current) === null || _a === void 0 ? void 0 : _a.isValid) || !((_b = billingAddressValidityStateRef.current) === null || _b === void 0 ? void 0 : _b.isValid)) {
            var warnResponse = {
                ok: false,
                status: 'warning',
                paymentMethod: SupportedPaymentMethodMappings.webDropInCreditCard,
                reason: 'Invalid Payment Information.'
            };
            if (!((_c = billingAddressValidityStateRef.current) === null || _c === void 0 ? void 0 : _c.isValid) && billingAddressValidityStateRef.current) {
                var addressErrors = AddressErrorHandling(billingAddressValidityStateRef.current);
                warnResponse = __assign(__assign({}, warnResponse), { errors: { billingInformation: { address: addressErrors } } });
            }
            props.onPaymentFailure(warnResponse);
            props.onProcessing(false);
            return;
        }
        if (props.applicationSession) {
            props.applicationSession.config.billingInformation = billingAddressRef.current;
        }
        (_d = props.onSubmit) === null || _d === void 0 ? void 0 : _d.call(props, __assign(__assign({}, formFields), { cardholderName: cardholderNameRef.current, billingInformation: billingAddressRef.current }));
    };
    var onSupportedCardBrands = function (brandList) {
        var mappedBrandList = brandMapping(SupportedVendors.braintree, brandList);
        setSupportedCardBrand(mappedBrandList);
    };
    useEffect(function () {
        var _a;
        (_a = props.applicationSession) === null || _a === void 0 ? void 0 : _a.initializeCreditCard(formRef, { onSubmit: onSubmit, onCardFieldsStatusChange: onCardFieldsStatusChange, onValidityChange: onValidityChange, onBrand: onBrand, onSupportedCardBrands: onSupportedCardBrands }, cardStyling, props.applicationSession.getSupportedCardBrands(), props.onCreditCardInitialized);
    }, [props.locale]);
    return (React.createElement(React.Fragment, null,
        React.createElement(CreditCardWithCardholderName, { ref: formRef },
            React.createElement(CreditCardFields, { invalidFields: invalidFields, cardFieldStatuses: cardFieldStatuses, supportedCardBrands: supportedCardBrand, selectedCardBrand: selectedCardBrand, cardholderName: cardholderName, setCardholderName: setCardholderName }),
            React.createElement(BillingAddressField, { vendor: props.vendor, existingAddress: (_e = props.existingBillingInformation) === null || _e === void 0 ? void 0 : _e.address, address: billingInformation === null || billingInformation === void 0 ? void 0 : billingInformation.address, onChange: onChangeBillingAddress, locale: props.locale, validityState: validityState === null || validityState === void 0 ? void 0 : validityState.billingAddressValidity }),
            props.hideContinueButton !== true && (React.createElement(SubmitButton, { id: "".concat(ComponentId.BUTTON_SUBMIT_PAYMENT, "_").concat(props.locale), label: creditCardLocaleConfig.Labels.continueButton })))));
});
export default CreditCard;
var templateObject_1, templateObject_2;
